import { FC, useState, useEffect, ChangeEvent } from 'react';

import { useUpdateWalletStatusMutation } from '@entities/wallets';
import type { IWallet, IWalletClickhouse } from '@entities/wallets';
import { Switch } from '@mui/material';
import { FormItemWithError } from '@shared/components/Form';

interface IProps {
  wallet: IWallet | IWalletClickhouse;
  refetch: () => void;
}

export const StatusToggler: FC<IProps> = ({ wallet, refetch }) => {
  const [updateWalletStatus, { error, isError, isLoading, isSuccess }] =
    useUpdateWalletStatusMutation();

  const [localValue, setLocalValue] = useState<string | null>(wallet.status);
  const [pendingValue, setPendingValue] = useState<string | null>(null);

  useEffect(() => {
    if (pendingValue === null) {
      setLocalValue(wallet.status);
    }
  }, [wallet.status, pendingValue]);

  useEffect(() => {
    if (isSuccess && pendingValue !== null) {
      setLocalValue(pendingValue);
      setPendingValue(null);
      refetch();
    }
  }, [isSuccess, pendingValue, refetch]);

  useEffect(() => {
    if (isError && pendingValue !== null) {
      setPendingValue(null);
    }
  }, [isError, pendingValue]);

  const handleChange = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const newValue = checked ? 'live' : 'inactive';

    setPendingValue(newValue);
    updateWalletStatus({ id: wallet.id, status: newValue }).unwrap();
  };

  return (
    <FormItemWithError name={'status'} error={error} isError={isError}>
      <Switch
        checked={localValue === 'live'}
        onChange={handleChange}
        disabled={isLoading || pendingValue !== null}
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--accent)' },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: 'var(--accent)',
          },
        }}
      />
    </FormItemWithError>
  );
};
